import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SiteNav from '@/components/SiteNav';
import SiteFooter from '@/components/SiteFooter';
import data from '@/seo/pages.json';
import { findPage } from '@/seo/core.mjs';

// One public page per service (/websites, /church-websites, /domains, ...).
// Words come from src/seo/pages.json so the page, its search metadata and the
// prerendered HTML (scripts/prerender-seo.mjs) always say the same thing.
function CtaLink({ cta, className }) {
  return (
    <Link to={cta.to} className={className}>
      {cta.label} <ArrowRight className="w-5 h-5" />
    </Link>
  );
}

export default function ServicePage({ path }) {
  const page = findPage(data, path);
  if (!page) return null;
  const related = (page.related || []).map((p) => findPage(data, p)).filter(Boolean);
  const ctaClass = 'inline-flex items-center justify-center gap-2 min-h-[52px] bg-indigo-500 hover:bg-indigo-600 text-white px-8 rounded-lg text-lg font-semibold transition-all active:scale-95 shadow-lg shadow-indigo-500/25';

  return (
    <div className="min-h-screen bg-slate-950 overflow-x-hidden text-slate-300" data-testid={`service-page-${path.slice(1)}`}>
      <SiteNav />

      <main>
        <section className="pt-28 pb-12 md:pt-36 md:pb-16 px-4 sm:px-6 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-transparent pointer-events-none" />
          <div className="relative max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>{page.h1}</h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8">{page.lede}</p>
            {page.cta && <CtaLink cta={page.cta} className={ctaClass} />}
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
          {(page.sections || []).map((section) => (
            <section key={section.heading} className="mt-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{section.heading}</h2>
              <div className="mt-4 space-y-4 text-lg leading-relaxed">
                {(section.paragraphs || []).map((p) => <p key={p}>{p}</p>)}
                {section.list && (
                  <ul className="space-y-3">
                    {section.list.map((li) => (
                      <li key={li} className="flex items-start gap-3"><Check className="w-5 h-5 mt-1 shrink-0 text-indigo-400" aria-hidden="true" />{li}</li>
                    ))}
                  </ul>
                )}
                {section.steps && (
                  <ol className="list-decimal pl-6 space-y-3 marker:text-indigo-400">
                    {section.steps.map((li) => <li key={li}>{li}</li>)}
                  </ol>
                )}
                {section.link && (
                  <p><a href={section.link.href} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-white underline underline-offset-4">{section.link.label}</a></p>
                )}
              </div>
            </section>
          ))}

          {page.faqs && page.faqs.length > 0 && (
            <section className="mt-14">
              <h2 className="text-2xl md:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Questions</h2>
              <dl className="mt-4 divide-y divide-slate-800">
                {page.faqs.map((f) => (
                  <div key={f.q} className="py-5">
                    <dt className="text-lg font-semibold text-white">{f.q}</dt>
                    <dd className="mt-2 text-lg text-slate-400 leading-relaxed">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {related.length > 0 && (
            <section className="mt-14">
              <h2 className="text-2xl md:text-3xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Related</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {related.map((r) => (
                  <li key={r.path}>
                    <Link to={r.path} className="flex items-center justify-between gap-3 min-h-[52px] rounded-xl border border-slate-800 bg-slate-900/60 px-5 py-3 text-white hover:border-indigo-500/50 transition-colors">
                      {r.navLabel}<ArrowRight className="w-4 h-4 text-indigo-400 shrink-0" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {page.cta && (
          <section className="px-6 pt-12 pb-20 text-center border-t border-slate-800 bg-indigo-500/5">
            <h2 className="text-3xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit' }}>Ready when you are.</h2>
            <CtaLink cta={page.cta} className={ctaClass} />
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
